import { useBottomSheetScrollableCreator } from '@gorhom/bottom-sheet';
import { Search02Icon } from '@hugeicons-pro/core-solid-rounded';
import { Tick02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { FlashList } from '@shopify/flash-list';
import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, View, TouchableOpacity } from 'react-native';

import { Checkbox, CheckboxIndicator, CheckboxIcon } from '@/components/gluestack/checkbox';
import { HStack } from '@/components/gluestack/hstack';
import { CheckIcon, CloseIcon } from '@/components/gluestack/icon';
import { Input, InputField, InputSlot, InputIcon } from '@/components/gluestack/input';
import { Pressable } from '@/components/gluestack/pressable';
import { Text } from '@/components/gluestack/text';
import { VStack } from '@/components/gluestack/vstack';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import {
  IContextSheetContentProps,
  ISheetConfig,
  DRAWER_SCROLL_CLASSES,
} from '@/components/ui/ContextSheet';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';

export interface IMultiSelectOption {
  key: string;
  label: string;
}

export interface IMultiSelectSheetProps extends IContextSheetContentProps {
  options?: IMultiSelectOption[];
  selectedKeys?: string[];
  onSelectionChange?: (keys: string[]) => void;
  searchable?: boolean;
  title?: string;
  renderOptionIcon?: (key: string) => React.ReactNode;
}

export default function MultiSelectSheet({
  onClose,
  payload,
  options: directOptions,
  selectedKeys: directSelectedKeys,
  onSelectionChange: directOnSelectionChange,
  searchable = true,
}: IMultiSelectSheetProps & { payload?: Partial<IMultiSelectSheetProps> }) {
  const options = (payload?.options ?? directOptions ?? []) as IMultiSelectOption[];
  const selectedKeys = payload?.selectedKeys ?? directSelectedKeys ?? [];
  const onSelectionChange = payload?.onSelectionChange ?? directOnSelectionChange;
  const renderOptionIcon = payload?.renderOptionIcon;
  const isLargeScreen = useIsLargeScreen();
  const gorhomFlashListScroll = useBottomSheetScrollableCreator();

  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [localSelection, setLocalSelection] = useState<Set<string>>(new Set(selectedKeys ?? []));
  const selectionRef = useRef<Set<string>>(localSelection);
  const didCommitRef = useRef(false);

  const commitSelection = useCallback(() => {
    didCommitRef.current = true;
    onSelectionChange?.(Array.from(selectionRef.current));
  }, [onSelectionChange]);

  useEffect(() => {
    selectionRef.current = localSelection;
  }, [localSelection]);

  useEffect(() => {
    return () => {
      // Commit selection when the sheet is dismissed (backdrop swipe, header close, etc.)
      if (!didCommitRef.current) {
        commitSelection();
      }
    };
  }, [commitSelection]);

  const filteredOptions = useMemo(() => {
    if (!options || options.length === 0) return [];
    if (!searchQuery.trim()) return options;
    const q = searchQuery.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [options, searchQuery]);

  const toggleOption = useCallback((key: string) => {
    setLocalSelection((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);

  const handleDone = useCallback(() => {
    commitSelection();
    onClose?.();
  }, [commitSelection, onClose]);

  const handleClear = useCallback(() => {
    setLocalSelection(new Set());
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const renderItem = useCallback(
    ({ item }: { item: IMultiSelectOption }) => {
      const isSelected = localSelection.has(item.key);
      return (
        <Pressable
          onPress={() => toggleOption(item.key)}
          className="flex-row items-center gap-3 px-2 py-3 active:bg-background-50 web:cursor-pointer web:hover:bg-background-50">
          <Checkbox value={item.key} isChecked={isSelected} pointerEvents="none" size="md">
            <CheckboxIndicator
              className={`${Platform.OS === 'web' ? '' : 'rounded-md'} ${isSelected ? '!border-primary-500 !bg-primary-500' : ''}`}>
              <CheckboxIcon as={CheckIcon} className="!color-white" />
            </CheckboxIndicator>
          </Checkbox>
          {renderOptionIcon?.(item.key)}
          <Text className="flex-1">{item.label}</Text>
        </Pressable>
      );
    },
    [localSelection, toggleOption, renderOptionIcon]
  );

  const selectedCount = localSelection.size;
  const optionsList = (
    <FlashList<IMultiSelectOption>
      {...DRAWER_SCROLL_CLASSES}
      data={filteredOptions}
      renderItem={renderItem}
      keyExtractor={(item) => item.key}
      keyboardShouldPersistTaps="handled"
      {...(!isLargeScreen ? { renderScrollComponent: gorhomFlashListScroll } : {})}
    />
  );

  return (
    <VStack className="flex-1 gap-2">
      {searchable && options && options.length > 10 && (
        <Input variant="outline" className="mx-1">
          <InputSlot className="pl-3">
            <HugeIconsWrapper icon={Search02Icon} size={16} className="color-typography-700" />
          </InputSlot>
          <InputField
            placeholder={t('common.search', 'Search...')}
            value={searchQuery}
            onChangeText={setSearchQuery}
            autoFocus={false}
            autoCorrect={false}
            autoCapitalize="none"
            autoComplete={Platform.OS === 'web' ? ('off' as any) : undefined}
          />
          {searchQuery?.length ? (
            <InputSlot onPress={clearSearch} className="pr-3">
              <InputIcon as={CloseIcon} />
            </InputSlot>
          ) : null}
        </Input>
      )}

      <View className="min-h-[200px] flex-1">{optionsList}</View>

      <HStack className="items-center justify-between gap-2 border-t border-background-100 px-1 pt-3">
        <HStack className="items-center gap-2">
          {selectedCount > 0 && (
            <TouchableOpacity onPress={handleClear}>
              <Text size="sm" className="text-primary-500">
                {t('common.clearAll', 'Clear all')}
              </Text>
            </TouchableOpacity>
          )}
          <Text size="sm" className="text-typography-500">
            {selectedCount > 0
              ? t('search.selectedCount', '{{count}} selected', { count: selectedCount })
              : t('search.noneSelected', 'None selected')}
          </Text>
        </HStack>
        <ButtonWrapper
          icon={Tick02Icon}
          text={t('common.done', 'Done')}
          onPress={handleDone}
          size="sm"
        />
      </HStack>
    </VStack>
  );
}

MultiSelectSheet.sheetConfig = {
  largeScreenMode: 'drawer',
  size: 'sm',
  height: 'full',
  scrollableContent: true,
  integratedNativeSheetScrollGestures: true,
} satisfies ISheetConfig;
